import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Calendar, MapPin, ArrowRight, Mail } from "lucide-react";

const fallbackPastEvents = [
  {
    title: "Recovery Connections of Central Florida - Golf Fundraiser",
    date: "September 12, 2021",
    location: "Central Florida",
    image_path: "/events/event-golf.jpg",
    description: "PARS proudly partnered with Recovery Connections of Central Florida for their annual golf fundraiser supporting people in long-term recovery.",
  },
  {
    title: "APALD Rally - A Public Awareness Lighting Day",
    date: "June 4, 2021",
    location: "Orlando, FL",
    image_path: "/events/event-apald.jpg",
    description: "Families, advocates, and survivors gathered at the APALD Rally to honor loved ones lost and demand better resources for those still struggling.",
  },
  {
    title: "Jewish Community Center Panel Discussion",
    date: "May 27, 2021",
    location: "JCC, Orlando, FL",
    image_path: "/events/event-jcc.jpg",
    description: "PARS board members joined community leaders for a panel discussion on stigma, substance use disorder, and how families can support one another.",
  },
  {
    title: "APALD Rally - Standing Together",
    date: "June 4, 2021",
    location: "Orlando, FL",
    image_path: "/events/event-rally.jpg",
    description: "A second moment from the APALD Rally - a sea of purple, every shirt a name, every name a loved one we refuse to forget.",
  },
];

export default function Events() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/events");
        setItems(data);
      } catch { setItems([]); }
    })();
  }, []);

  const upcoming = (items || []).filter((e) => e.kind === "upcoming");
  const past = (items || []).filter((e) => e.kind === "past");
  const pastToShow = past.length > 0 ? past : fallbackPastEvents;
  return (
    <div data-testid="events-page">
      <section className="bg-[var(--rts-blue-pale)] py-20">
        <div className="max-w-6xl mx-auto px-6">
          <span className="inline-block text-xs font-bold tracking-[0.25em] uppercase text-[var(--rts-red)] mb-4">
            Events
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--rts-ink)] leading-[1.05]">
            Show up. <span className="italic text-[var(--rts-blue-deep)]">Stand together.</span>
          </h1>
          <p className="mt-5 text-lg text-[var(--rts-ink-soft)] max-w-3xl leading-relaxed">
            Rallies, panels, fundraisers, and community gatherings. Every event is a chance to honor lives lost and lift up the people still fighting.
          </p>
        </div>
      </section>

      {/* Naloxone distribution callout */}
      <section className="bg-[var(--rts-red)] text-white py-14" data-testid="events-naloxone-callout">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8">
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-white/85 mb-2">Community Distribution</p>
            <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-3">We distribute free Naloxone (Narcan).</h2>
            <p className="text-white/90 leading-relaxed">
              PARS hosts naloxone distribution events across Central Florida. Want us to bring kits and training to your community, school, church, or workplace? Contact us - we'll set it up.
            </p>
          </div>
          <div className="md:col-span-4 md:justify-self-end">
            <a href="/connect-with-us" className="inline-flex items-center gap-2 bg-white text-[var(--rts-red)] hover:bg-white/90 px-6 py-3 rounded-full font-bold shadow-lg transition hover:-translate-y-0.5" data-testid="events-naloxone-contact">
              Contact Us for Distribution
            </a>
          </div>
        </div>
      </section>

      {/* Upcoming */}
      <section className="bg-white py-20" data-testid="upcoming-events">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <span className="inline-block text-xs font-bold tracking-[0.25em] uppercase text-[var(--rts-red)] mb-2">
                What's next
              </span>
              <h2 className="font-display text-3xl sm:text-4xl text-[var(--rts-ink)]">Upcoming Events</h2>
            </div>
          </div>

          <div className="bg-[var(--rts-blue-pale)] border-2 border-dashed border-[var(--rts-blue)]/30 rounded-3xl p-10 md:p-14 text-center">
            <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-[var(--rts-blue-deep)] grid place-items-center">
              <Calendar className="w-8 h-8 text-white" />
            </div>
            <h3 className="font-display text-2xl sm:text-3xl text-[var(--rts-ink)] mb-3">
              New events are being scheduled.
            </h3>
            <p className="text-[var(--rts-ink-soft)] max-w-xl mx-auto leading-relaxed mb-6">
              Want to be the first to know when our next rally, panel, or fundraiser drops? Drop us a note and we'll keep you in the loop - or follow us on social.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/connect-with-us" className="inline-flex items-center gap-2 bg-[var(--rts-red)] hover:bg-[var(--rts-red-deep)] text-white px-6 py-3 rounded-full font-bold shadow-md transition hover:-translate-y-0.5" data-testid="events-notify-cta">
                <Mail className="w-4 h-4" /> Notify Me
              </Link>
              <a href="https://www.facebook.com/PARSProjectAddictionReversingtheStigma/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-[var(--rts-blue-deep)] border-2 border-[var(--rts-blue)]/30 px-6 py-3 rounded-full font-bold hover:border-[var(--rts-blue)] transition">
                Follow on Facebook
              </a>
            </div>
          </div>

          {upcoming.length > 0 && (
            <div className="mt-8 grid md:grid-cols-2 gap-6">
              {upcoming.map((e, i) => (
                <article key={e.id || i} className="bg-white rounded-2xl border-2 border-[var(--rts-blue-pale)] overflow-hidden hover:border-[var(--rts-blue)] transition group" data-testid={`upcoming-event-${i}`}>
                  {e.image_path && (
                    <div className="aspect-[16/10] overflow-hidden bg-[var(--rts-blue-pale)]">
                      <img src={fileUrl(e.image_path)} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex flex-wrap items-center gap-3 text-xs text-[var(--rts-muted)] mb-3">
                      {e.date && <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {e.date}{e.time ? ` · ${e.time}` : ""}</span>}
                      {e.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {e.location}</span>}
                    </div>
                    <h3 className="font-display text-xl text-[var(--rts-ink)] mb-2 leading-tight">{e.title}</h3>
                    {e.description && <p className="text-sm text-[var(--rts-ink-soft)] leading-relaxed">{e.description}</p>}
                    {e.registration_url && (
                      <a href={e.registration_url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 bg-[var(--rts-red)] hover:bg-[var(--rts-red-deep)] text-white px-5 py-2.5 rounded-full font-bold text-sm min-h-[44px]">
                        Register / Info <ArrowRight className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Past Events */}
      <section className="bg-[var(--rts-ink)] text-white py-20" data-testid="past-events">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-12">
            <span className="inline-block text-xs font-bold tracking-[0.25em] uppercase text-[var(--rts-blue-light)] mb-2">
              Looking back
            </span>
            <h2 className="font-display text-4xl sm:text-5xl">Past Events</h2>
            <p className="mt-3 text-white/70 max-w-2xl">Moments from our journey - every one a reminder that we are not alone.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {pastToShow.map((e, i) => (
              <article
                key={e.id || i}
                className="bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10 hover:border-[var(--rts-blue)] transition group"
                data-testid={`past-event-${i}`}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={fileUrl(e.image_path || e.image)}
                    alt={e.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex flex-wrap items-center gap-3 text-xs text-white/65 mb-3">
                    {e.date && <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {e.date}</span>}
                    {e.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {e.location}</span>}
                  </div>
                  <h3 className="font-display text-xl mb-2 leading-tight">{e.title}</h3>
                  <p className="text-sm text-white/75 leading-relaxed">{e.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[var(--rts-blue-deep)] text-white py-16">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl mb-2">Host an event with PARS</h2>
            <p className="text-white/80">Schools, churches, civic groups - we'll come speak, share, and stand with you.</p>
          </div>
          <Link to="/connect-with-us" className="inline-flex items-center gap-2 bg-[var(--rts-red)] hover:bg-[var(--rts-red-deep)] px-7 py-3.5 rounded-full font-bold shadow-lg transition hover:-translate-y-0.5" data-testid="events-host-cta">
            Get in Touch <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
